import type { ReactNode } from "react";
import { Navigate, useParams, useSearchParams } from "react-router";

import { useOrganization } from "@/auth/organization";
import {
  ActionsBlocked,
  DisputeBlocked,
  Alert,
  ErrorState,
  FullPageSpinner,
  Badge,
  Button,
  Breadcrumb,
  Tabs,
  type Requirement,
  type TabItem,
} from "@/components/ui";
import {
  domainInitials,
  DISPUTE_LOCKED,
  domainPill,
  longDate,
  relativeDays,
  useDomainDetail,
  verificationApplies,
  type DomainDetail,
} from "@/lib/domains";

import { LifecycleView } from "./lifecycle";
import { ContactsTab } from "./tabs/Contacts";
import { DnsTab } from "./tabs/Dns";
import { EmailTab } from "./tabs/Email";
import { ForwardingTab } from "./tabs/Forwarding";
import { NameserversTab } from "./tabs/Nameservers";
import { OverviewTab } from "./tabs/Overview";
import { SecurityTab } from "./tabs/Security";
import { TransfersTab } from "./tabs/Transfers";
import { Heading, HeadTile, DomainPage } from "@/components/domains/parts";

export type TabKey =
  "overview" | "dns" | "email" | "forwarding" | "nameservers" | "security" | "contacts" | "transfers";

const TABS: TabItem<TabKey>[] = [
  { key: "overview", label: "Overview" },
  { key: "dns", label: "DNS" },
  { key: "email", label: "Email" },
  { key: "forwarding", label: "Forwarding" },
  { key: "nameservers", label: "Nameservers" },
  { key: "security", label: "Security" },
  { key: "contacts", label: "Contacts" },
  { key: "transfers", label: "Transfers" },
];
const CRUMB: Record<TabKey, string> = {
  overview: "Overview",
  dns: "DNS",
  email: "Email",
  forwarding: "Forwarding",
  nameservers: "Nameservers",
  security: "Security",
  contacts: "Contact",
  transfers: "Transfers",
};

const SUSPENDED = "This domain is suspended. Contact support.";

/** The Domain Control Centre: one registered domain, tab by tab (Figma 4073-6156 and siblings). */
export function DomainDetailPage() {
  const { domainId } = useParams();
  const [params, setParams] = useSearchParams();
  const org = useOrganization();
  const detail = useDomainDetail(domainId);

  if (detail.isPending) return <FullPageSpinner />;
  if (detail.isError) return <ErrorState error={detail.error} onRetry={() => void detail.refetch()} />;
  const d = detail.data.domain;
  // The Email tab is for people who may read e-mail on this domain's folder (the Email Manager role); a link to it
  // (from a notification) opens Overview for everyone else.
  const canEmail = org.canOn("email:read", "domain_folder", detail.data.folder?.id);
  const tabs = TABS.filter((t) => t.key !== "email" || canEmail);
  const tab = (tabs.some((t) => t.key === params.get("tab")) ? params.get("tab") : "overview") as TabKey;

  // Still being bought: its order page tells the story.
  if (
    (d.status === "registering" || d.status === "transferring_in" || d.status === "restoring") &&
    detail.data.open_order_id
  )
    return <Navigate to={`/domains/orders/${detail.data.open_order_id}`} replace />;
  if (d.status !== "active") return <LifecycleView detail={detail.data} />;

  const openModal = (modal: string) =>
    setParams((p) => {
      p.set("modal", modal);
      return p;
    });
  const heading = headingFor(tab, detail.data, openModal);

  // Suspended by CortexGrip support (PHP-3934): it can be read, but every change waits for support.
  const reference = detail.data.abuse_suspension_reference;
  const blocked = d.suspended
    ? reference
      ? `This domain is suspended following an abuse report. Contact support quoting ${reference}.`
      : SUSPENDED
    : null;

  return (
    <ActionsBlocked.Provider value={blocked}>
      <DisputeBlocked.Provider value={d.dispute_locked ? DISPUTE_LOCKED : null}>
        <DomainPage>
          {blocked && <Alert tone="danger">{blocked}</Alert>}
          {d.dispute_locked && <Alert tone="warning">{DISPUTE_LOCKED}</Alert>}
          <Breadcrumb
            items={[
              { label: "Domains", to: "/domains" },
              { label: "...", to: "/domains" },
              { label: d.display_name, to: tab === "overview" ? undefined : `/domains/${d.id}` },
              { label: CRUMB[tab] },
            ]}
          />
          <Heading
            tile={<HeadTile>{domainInitials(d.fqdn)}</HeadTile>}
            title={heading.title}
            status={heading.status}
            subtitle={heading.subtitle}
            actions={heading.action}
          />
          <Tabs
            tabs={tabs}
            active={tab}
            onChange={(key) =>
              setParams(
                (p) => {
                  if (key === "overview") p.delete("tab");
                  else p.set("tab", key);
                  p.delete("modal");
                  return p;
                },
                { replace: true },
              )
            }
          />
          {tab === "overview" && <OverviewTab detail={detail.data} />}
          {tab === "dns" && <DnsTab detail={detail.data} />}
          {tab === "email" && <EmailTab detail={detail.data} />}
          {tab === "forwarding" && <ForwardingTab detail={detail.data} />}
          {tab === "nameservers" && <NameserversTab detail={detail.data} />}
          {tab === "security" && <SecurityTab detail={detail.data} />}
          {tab === "contacts" && <ContactsTab detail={detail.data} />}
          {tab === "transfers" && <TransfersTab detail={detail.data} />}
        </DomainPage>
      </DisputeBlocked.Provider>
    </ActionsBlocked.Provider>
  );
}

function headingFor(
  tab: TabKey,
  detail: DomainDetail,
  openModal: (modal: string) => void,
): { title: ReactNode; status?: ReactNode; subtitle: ReactNode; action?: ReactNode } {
  const d = detail.domain;
  // Shown to everyone who can open the domain; unavailable, with the reason, without domains:manage on its folder.
  const manage: Requirement = { permission: "domains:manage", on: ["domain_folder", detail.folder?.id] };
  const action = (label: string, modal: string, variant: "primary" | "danger" = "primary") => (
    <Button
      variant={variant === "primary" ? "brand" : "danger"}
      size="lg"
      requires={manage}
      onClick={() => openModal(modal)}
    >
      {label}
    </Button>
  );
  const name = d.display_name;
  switch (tab) {
    case "dns":
      return {
        title: name,
        status:
          d.nameserver_mode === "custom" ? (
            <Badge size="md" tone="neutral">
              DNS managed elsewhere
            </Badge>
          ) : (
            <Badge size="md" tone="success">
              DNS managed by CortexGrip
            </Badge>
          ),
        subtitle:
          d.nameserver_mode === "custom"
            ? "This domain uses other nameservers, so its records live with that DNS provider"
            : "Simple by default - technical records live under Advanced",
      };
    case "nameservers":
      return {
        title: name,
        status:
          d.nameserver_mode === "custom" ? (
            <Badge size="md" tone="warning">
              Custom nameservers
            </Badge>
          ) : (
            <Badge size="md" tone="success">
              CortexGrip nameservers
            </Badge>
          ),
        subtitle:
          d.nameserver_mode === "custom"
            ? "DNS is hosted elsewhere — records must be managed at that provider"
            : "Default hosting — DNS records are managed for you automatically",
        action: detail.capabilities.includes("nameservers")
          ? action("Custom nameserver", "custom-ns")
          : undefined,
      };
    case "email":
      return {
        title: `${name} - Email`,
        subtitle: "Whether the DNS records professional email needs are right",
      };
    case "forwarding":
      return {
        title: `${name} - Forwarding`,
        subtitle: "Send visitors or mail at this domain somewhere else",
      };
    case "security":
      return { title: `${name} - Security`, subtitle: "Transfer protection and domain security settings" };
    case "contacts": {
      const pending =
        verificationApplies(detail.contact, detail.domain.fqdn) &&
        detail.contact?.verification === "pending" &&
        detail.contact.verification_due_at;
      return {
        title: `${name} - Contacts`,
        status: pending ? (
          <Badge size="md" tone="danger">
            Verification required
          </Badge>
        ) : undefined,
        subtitle: pending
          ? "ICANN requires confirming your registrant email"
          : "Who owns this domain on record",
        action: action("Add teammate", "teammate"),
      };
    }
    case "transfers":
      return {
        title: `${name} - Transfers`,
        subtitle: "Move this domain to or from CortexGrip",
        action: action("Transfer with Code", "transfer-in"),
      };
    default: {
      const pill = domainPill(d);
      const parts = [
        d.is_primary ? "Primary domain" : d.site ? `Connected to ${d.site.name}` : "Not connected",
        d.origin === "transferred" ? "transferred to CortexGrip" : "registered with CortexGrip",
        d.days_left !== null ? `${d.auto_renew ? "renews" : "expires"} ${relativeDays(d.days_left)}` : null,
        d.health === "renewal_failed" && d.renewal_next_attempt_at
          ? `next renewal attempt ${longDate(d.renewal_next_attempt_at)}`
          : null,
      ].filter(Boolean);
      return {
        title: name,
        status: (
          <Badge size="md" tone={pill.tone}>
            {pill.label}
          </Badge>
        ),
        subtitle: parts.join(" · "),
        action: d.health === "renewal_failed" ? action("Update payment", "payment", "danger") : undefined,
      };
    }
  }
}
